"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import { useRouter } from "next/navigation";
import { ArrowRight } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { readBrowserAttributionWithCurrentUrl } from "@/components/analytics/browser-attribution";

const CTA_TEXT = "Start the free fix";

function buildScopeDescription({
  rejectionText,
  jobText,
}: {
  rejectionText: string;
  jobText: string;
}) {
  return [
    rejectionText.trim()
      ? `Building control or client rejection comments:\n${rejectionText.trim()}`
      : null,
    jobText.trim() ? `Job description:\n${jobText.trim()}` : null,
  ]
    .filter(Boolean)
    .join("\n\n");
}

export function RamsRejectedStartForm() {
  const router = useRouter();
  const posthog = usePostHog();
  const [rejectionText, setRejectionText] = useState("");
  const [jobText, setJobText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasInput = rejectionText.trim().length > 0 || jobText.trim().length > 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!hasInput || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    const attribution = readBrowserAttributionWithCurrentUrl();
    const attributionPayload = attribution ?? {};
    const scopeDescription = buildScopeDescription({ rejectionText, jobText });

    try {
      const createRes = await fetch("/api/draft-projects", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roleIntent: "RUNNING_THE_JOB",
          title: "RAMS rejection fix",
          ...attributionPayload,
          utmOffer: attribution?.utmOffer ?? "rams_rejected_free_fix",
        }),
      });

      if (!createRes.ok) {
        throw new Error("create_failed");
      }

      const draft = (await createRes.json()) as {
        sessionToken?: string;
      };

      if (!draft.sessionToken) {
        throw new Error("missing_session");
      }

      const patchRes = await fetch(`/api/draft-projects/${draft.sessionToken}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scopeDescription }),
      });

      if (!patchRes.ok) {
        throw new Error("patch_failed");
      }

      posthog?.capture?.("rams_rejected_free_fix_started", {
        cta_label: CTA_TEXT,
        has_rejection_comments: rejectionText.trim().length > 0,
        has_job_description: jobText.trim().length > 0,
        destination: "/start-project/brief",
      });

      router.push("/start-project/brief");
    } catch {
      setError(
        "We could not start the draft from this page. Use the standard free job pack flow and paste the comments into the job description.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-border bg-white p-5 shadow-sm sm:p-6"
    >
      <div>
        <label htmlFor="rejection-comments" className="text-sm font-bold text-navy">
          Paste the rejection comments
        </label>
        <textarea
          id="rejection-comments"
          value={rejectionText}
          onChange={(event) => setRejectionText(event.target.value)}
          rows={5}
          className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent-light"
          placeholder="Example: RAMS too generic, missing method statement sequence, no competent-person sign-off..."
        />
      </div>

      <div className="mt-4">
        <label htmlFor="job-description" className="text-sm font-bold text-navy">
          Or describe the job
        </label>
        <textarea
          id="job-description"
          value={jobText}
          onChange={(event) => setJobText(event.target.value)}
          rows={4}
          className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent-light"
          placeholder="Example: single-storey rear extension, drainage alterations, scaffold to rear elevation, knock-through and RSJ..."
        />
      </div>

      {error ? (
        <p role="alert" className="mt-4 text-sm font-semibold text-danger">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={!hasInput || isSubmitting}
        className="mt-5 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-accent px-5 py-3 text-base font-bold text-white transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? "Starting..." : CTA_TEXT}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </button>
      <p className="mt-3 text-xs leading-5 text-muted">
        No card required. You will add the site details next and review every document before use.
      </p>
    </form>
  );
}
